import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  InputSignal,
  Signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IField, IMask } from '@web/dtos';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TextInputComponent } from '../../../../../../components/data-input/form-inputs/text-input/text-input.component';
import { CheckboxComponent } from '../../../../../../components/data-input/form-inputs/checkbox/checkbox.component';
import { DateInputComponent } from '../../../../../../components/data-input/form-inputs/date-input/date-input/date-input.component';
import { DropDownComponent } from '../../../../../../components/data-input/form-inputs/drop-down/drop-down.component';
import { NumberInputComponent } from '../../../../../../components/data-input/form-inputs/number-input/number-input.component';
import { SectionHeaderComponent } from '../../../../../../components/layout/section/section-header/section-header.component';
import { SectionHeadlineComponent } from '../../../../../../components/layout/section/section-header/section-headline/section-headline.component';
import { SubscriptionConfigStore } from '../../../../../../services/api/subscription/subscription-config-store.service';
import { SectionComponent } from '../../../../../../components/layout/section/section.component';
import { SubscriptionFormService } from '../../../../../../services/subscription/subscription-form.service';
import { ESubscriptionDataTypes } from '@web/dtos';

@Component({
  selector: 'aw-dynamic-mask-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TextInputComponent,
    CheckboxComponent,
    DateInputComponent,
    DropDownComponent,
    NumberInputComponent,
    SectionHeaderComponent,
    SectionHeadlineComponent,
    SectionComponent,
  ],
  templateUrl: './dynamic-mask-form.component.html',
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DynamicMaskFormComponent {
  mask: InputSignal<IMask> = input.required();
  protected readonly formBuilder: FormBuilder = inject(FormBuilder);

  protected readonly SubscriptionDataTypes: typeof ESubscriptionDataTypes =
    ESubscriptionDataTypes;
  private subscriptionStore: SubscriptionConfigStore = inject(
    SubscriptionConfigStore
  );
  private inlineMasks: IMask[] | undefined =
    this.subscriptionStore.inlineMasks();
  private subscriptionFormService: SubscriptionFormService = inject(
    SubscriptionFormService
  );

  public form!: FormGroup;

  protected readonly rows: Signal<IField[][]> = computed(() => {
    const rows: IField[][] = [[]];
    this.mask().fields.forEach((field, i) => {
      while (rows.length < field.row) {
        rows.push([]);
      }
      rows[field.row - 1].push(field);
    });
    rows.forEach((row: IField[]) => {
      row.sort((a: IField, b: IField) => (a.colStart < b.colStart ? -1 : 1));
    });
    return rows;
  });

  constructor() {
    effect(() => {
      const mask: IMask = this.mask();
      if (!mask) return;
      this.form = this.subscriptionFormService.getOrCreateForm(mask);
    });
  }

  public getInlineMask(followingMaskGuid: string | null): IMask | undefined {
    if (!followingMaskGuid) {
      return undefined;
    }
    return this.inlineMasks?.find(m => m.id == followingMaskGuid);
  }

  public toDate(value: string | null): Date | undefined {
    if (!value) {
      return undefined;
    }
    return new Date(value);
  }

  public listDatesToDate(listDates: string[] | null): Date[] | null {
    if (!listDates) {
      return null;
    }
    return listDates.map(date => new Date(date));
  }

  public isCheckBoxChecked(field: IField): boolean {
    return this.form.get(field.internalName)?.value === true;
  }
}
